import api from "@/lib/api";
import { toast } from "sonner";

// Fetch the valuation report PDF through the authenticated axios client (sends the
// session cookie) so it works even when a raw new-tab navigation would not carry auth.
export async function openValuationReport(jobId, reference, { draft = false } = {}) {
  const t = toast.loading(draft ? "Preparing draft report…" : "Preparing valuation report…");
  try {
    // Cache-bust so a reprocessed report is always fetched fresh (never a stale browser copy).
    const bust = `_=${Date.now()}`;
    const { data } = await api.get(`/jobs/${jobId}/valuation-report.pdf?${draft ? "draft=1&" : ""}${bust}`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const win = window.open(url, "_blank");
    if (!win) {
      // popup blocked — fall back to download
      const a = document.createElement("a");
      a.href = url;
      a.download = `Valuation-${reference || jobId}${draft ? "-DRAFT" : ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    toast.dismiss(t);
  } catch (e) {
    toast.dismiss(t);
    const status = e?.response?.status;
    if (status === 401) toast.error("Your session expired. Please sign in again.");
    else if (status === 409) toast.error(e?.response?.data?.detail || "Resolve the report's deviations before releasing.");
    else if (status === 404) toast.error("Report not available for this job.");
    else toast.error("Could not generate the report. Please try again.");
  }
}

export async function releaseReportAndInvoice(jobId) {
  const { data } = await api.post(`/jobs/${jobId}/release`);
  return data;
}

export async function processReport(jobId) {
  const { data } = await api.post(`/jobs/${jobId}/process-report`);
  return data;
}

export async function emailValuationReport(jobId) {
  const t = toast.loading("Emailing report to the bank…");
  try {
    const { data } = await api.post(`/jobs/${jobId}/email-report`);
    toast.dismiss(t);
    toast.success(`Report emailed to ${data.recipient}`);
  } catch (e) {
    toast.dismiss(t);
    toast.error(e?.response?.data?.detail || "Could not email the report.");
  }
}

export async function openChecklistPdf(jobId, reference) {
  const t = toast.loading("Preparing checklist…");
  try {
    const { data } = await api.get(`/jobs/${jobId}/checklist.pdf?_=${Date.now()}`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const win = window.open(url, "_blank");
    if (!win) {
      const a = document.createElement("a");
      a.href = url;
      a.download = `Checklist-${reference || jobId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    toast.dismiss(t);
  } catch (e) {
    toast.dismiss(t);
    const status = e?.response?.status;
    if (status === 401) toast.error("Your session expired. Please sign in again.");
    else if (status === 404) toast.error("Checklist not available for this job.");
    else toast.error("Could not generate the checklist. Please try again.");
  }
}

export async function emailChecklist(jobId) {
  const t = toast.loading("Emailing checklist to the bank…");
  try {
    const { data } = await api.post(`/jobs/${jobId}/email-checklist`);
    toast.dismiss(t);
    toast.success(`Checklist emailed to ${data.recipient}`);
  } catch (e) {
    toast.dismiss(t);
    toast.error(e?.response?.data?.detail || "Could not email the checklist.");
  }
}
